import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import Navigation from "@/components/Navigation";
import PaidPlanModal from "@/components/PaidPlanModal";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import {
  TrendingUp,
  BookOpen,
  Heart,
  FlaskConical,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  Flame,
  History,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  Eye,
  FileText,
  ArrowRight,
  Play,
} from "lucide-react";

const SUBJECT_BG = { Anatomy: "bg-blue-500", Physiology: "bg-rose-500", Biochemistry: "bg-purple-500" };
const SUBJECT_LETTER = { Anatomy: "A", Physiology: "P", Biochemistry: "B" };

const getMarksAwarded = (attempt) =>
  attempt?.evaluation?.marks_awarded ?? attempt?.evaluation?.marks_awarded_total ?? attempt?.score_awarded ?? null;

const getMarksTotal = (attempt) =>
  attempt?.question_snapshot?.marks_total ?? attempt?.evaluation?.marks_total ?? attempt?.total_marks ?? null;

const getStreakDay = (createdAt) => {
  if (!createdAt) return 1;
  return Math.max(1, Math.floor((new Date() - new Date(createdAt)) / (1000 * 60 * 60 * 24)) + 1);
};

const getCoachLine = (subjects) => {
  if (!subjects?.length) return null;
  const live = subjects.filter(s => s.is_live);
  if (!live.length) return null;
  const top = live.slice().sort(
    (a, b) => ((b.question_count || 0) - (b.attempted_count || 0)) - ((a.question_count || 0) - (a.attempted_count || 0))
  )[0];
  const unattempted = (top.question_count || 0) - (top.attempted_count || 0);
  if (unattempted <= 0) return null;
  return { name: top.name, count: unattempted };
};

const FeedbackPanel = ({ fb }) => {
  if (!fb) return null;
  if (typeof fb !== "object") return (
    <div><p className="text-xs text-slate-500 mb-2">AI Feedback</p>
      <div className="p-4 bg-blue-50 border border-blue-100 rounded-xl"><p className="text-sm text-blue-800">{String(fb)}</p></div>
    </div>
  );
  return (
    <div><p className="text-xs text-slate-500 mb-2">AI Feedback</p>
      <div className="space-y-3">
        {fb.summary && <div className="p-4 bg-blue-50 border border-blue-100 rounded-xl"><p className="text-sm text-blue-800">{fb.summary}</p></div>}
        {fb.strengths?.length > 0 && <div className="p-3 bg-emerald-50 border border-emerald-100 rounded-xl">
          <p className="text-xs font-semibold text-emerald-700 mb-2">✓ What you got right</p>
          <ul className="space-y-1">{fb.strengths.map((s, i) => <li key={i} className="text-xs text-emerald-700">• {s}</li>)}</ul>
        </div>}
        {fb.gaps?.length > 0 && <div className="p-3 bg-amber-50 border border-amber-100 rounded-xl">
          <p className="text-xs font-semibold text-amber-700 mb-2">✗ What was missing</p>
          <ul className="space-y-1">{fb.gaps.map((g, i) => <li key={i} className="text-xs text-amber-700">• {g}</li>)}</ul>
        </div>}
        {fb.examiner_tip && <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl">
          <p className="text-xs font-semibold text-slate-600 mb-1">💡 Examiner Tip</p>
          <p className="text-xs text-slate-600">{fb.examiner_tip}</p>
        </div>}
      </div>
    </div>
  );
};

const StudentDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [subjects, setSubjects] = useState([]);
  const [stats, setStats] = useState(null);
  const [recentAttempts, setRecentAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [attemptDetailOpen, setAttemptDetailOpen] = useState(false);
  const [selectedAttempt, setSelectedAttempt] = useState(null);

  useEffect(() => { fetchDashboardData(); }, []);

  const fetchDashboardData = async () => {
    try {
      const [subjectsRes, statsRes, attemptsRes] = await Promise.all([
        axios.get(`${API}/subjects`),
        axios.get(`${API}/stats/dashboard`),
        axios.get(`${API}/attempts?limit=10`),
      ]);
      setSubjects(subjectsRes.data);
      setStats(statsRes.data);
      setRecentAttempts(attemptsRes.data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubjectClick = (subject) => {
    if (subject.is_live) navigate(`/practice/${subject.name}`);
    else toast.info("Coming soon! This subject is being prepared.");
  };

  const viewAttemptDetail = (attempt) => { setSelectedAttempt(attempt); setAttemptDetailOpen(true); };

  const getGreeting = () => {
    const h = new Date().getHours();
    return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
  };

  const getReadinessColor = (label) => {
    switch (label) {
      case "exam_ready": return "text-emerald-600 bg-emerald-100";
      case "borderline_safe": return "text-amber-600 bg-amber-100";
      case "needs_practice": return "text-orange-600 bg-orange-100";
      case "at_risk": return "text-red-600 bg-red-100";
      default: return "text-blue-600 bg-blue-100";
    }
  };

  const getReadinessLabel = (label) => {
    switch (label) {
      case "exam_ready": return "Exam Ready";
      case "borderline_safe": return "Borderline Safe";
      case "needs_practice": return "Needs Practice";
      case "at_risk": return "At Risk";
      default: return "Building momentum";
    }
  };

  const getScoreColor = (p) => p >= 70 ? "text-emerald-600" : p >= 50 ? "text-amber-600" : "text-red-500";
  const getScoreLabel = (p) => p >= 70 ? { text: "Excellent", color: "text-emerald-600" } : p >= 50 ? { text: "Improving", color: "text-amber-600" } : { text: "Needs work", color: "text-red-500" };

  const formatDate = (d) => new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

  const coach = getCoachLine(subjects);
  const isNewUser = (stats?.total_attempted || 0) === 0;

  const recommendedSubject = useMemo(() => {
    const live = subjects.filter(s => s.is_live);
    if (!live.length) return null;
    return live.slice().sort(
      (a, b) => ((b.question_count || 0) - (b.attempted_count || 0)) - ((a.question_count || 0) - (a.attempted_count || 0))
    )[0];
  }, [subjects]);

  // Free plan usage derived from user object
  const saUsed  = Math.max(0, 4 - (user?.monthly_short_answers_remaining  ?? 4));
  const seUsed  = Math.max(0, 2 - (user?.monthly_short_essays_remaining   ?? 2));
  const leUsed  = Math.max(0, 1 - (user?.monthly_long_essays_remaining    ?? 1));

  if (loading) return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-[#2E3A59] border-t-transparent rounded-full animate-spin" />
        <p className="text-[#2E3A59] font-medium">Loading dashboard...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navigation role="student" />

      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">

          {/* ── WELCOME BANNER ── */}
          <div
            className="bg-gradient-to-r from-[#2E3A59] to-[#1A2236] rounded-2xl p-5 mb-6 text-white"
            data-testid="dashboard-welcome-banner"
          >
            <div className="flex flex-col lg:flex-row lg:items-stretch gap-4 lg:gap-6">

              {/* Left */}
              <div className="flex-1 min-w-0 flex flex-col justify-between gap-3">
                <div>
                  <h1 className="text-xl lg:text-2xl font-heading font-bold mb-1">
                    {getGreeting()}, {user?.name?.split(" ")[0] || "Student"} 👋
                  </h1>
                  <p className="text-slate-300 text-sm mb-3">
                    {user?.university || "RGUHS"} · Sem {user?.semester || 1} · {user?.college || "Medical College"}
                  </p>

                  {/* Coach nudge pill — matches mockup green pill */}
                  {coach && (
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-500/20 border border-emerald-400/40 rounded-lg mb-3">
                      <span className="text-xs font-semibold text-emerald-300">
                        Start with {coach.name}: {coach.count} unanswered questions
                      </span>
                    </div>
                  )}
                </div>

                {/* CTAs — plain style matching mockup */}
                <div className="flex gap-3 flex-wrap">
                  <button
                    onClick={() => recommendedSubject ? navigate(`/practice/${recommendedSubject.name}`) : navigate("/practice")}
                    className="flex items-center gap-2 px-5 py-2 bg-white text-[#2E3A59] rounded-lg font-semibold text-sm hover:bg-slate-100 transition-colors"
                    data-testid="dashboard-continue-practice-btn"
                  >
                    <Play className="w-4 h-4" /> Continue Practice
                  </button>
                  <button
                    onClick={() => navigate("/attempts")}
                    className="flex items-center gap-2 px-5 py-2 bg-white/10 border border-white/20 text-white rounded-lg font-semibold text-sm hover:bg-white/20 transition-colors"
                    data-testid="dashboard-view-attempts-btn"
                  >
                    <History className="w-4 h-4" /> View Recent Attempts
                  </button>
                </div>
              </div>

              {/* Right — stats matching mockup layout */}
              <div className="flex flex-col gap-2 lg:w-56 flex-shrink-0">
                <div className="grid grid-cols-2 gap-2 flex-1">
                  <div className="bg-white/10 rounded-xl p-3">
                    <p className="text-xs text-slate-400 mb-1">Attempted</p>
                    <p className="text-2xl font-bold" data-testid="stat-attempted">{stats?.total_attempted || 0}</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-3">
                    <p className="text-xs text-slate-400 mb-1">Avg score</p>
                    <p className="text-2xl font-bold text-emerald-400" data-testid="stat-avg-score">{stats?.average_score || 0}%</p>
                  </div>
                </div>
                <div className="bg-white/10 rounded-xl p-3">
                  <p className="text-xs text-slate-400 mb-0.5">Current status</p>
                  <p className="text-sm font-bold text-white">{getReadinessLabel(stats?.readiness)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* ── MAIN GRID ── */}
          <div className="grid lg:grid-cols-12 gap-6">

            {/* LEFT */}
            <div className="lg:col-span-8 space-y-6">

              {/* Choose a Subject */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading text-lg lg:text-xl font-bold text-[#2E3A59]">Choose a Subject</h2>
                  <span className="text-xs text-slate-400">Sem {user?.semester || 1}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="subjects-grid">
                  {subjects.map((subject) => {
                    const bg  = SUBJECT_BG[subject.name]     || "bg-slate-500";
                    const lt  = SUBJECT_LETTER[subject.name] || subject.name[0];
                    const isRec = recommendedSubject?.name === subject.name;
                    const unans = Math.max(0, (subject.question_count || 0) - (subject.attempted_count || 0));

                    return (
                      <div
                        key={subject.name}
                        onClick={() => handleSubjectClick(subject)}
                        className={`bg-white rounded-xl border p-4 transition-all group cursor-pointer relative ${
                          subject.is_live ? "border-slate-200 hover:border-[#2E3A59] hover:shadow-md" : "border-slate-100 opacity-60 cursor-not-allowed"
                        }`}
                        data-testid={`subject-card-${subject.name.toLowerCase()}`}
                      >
                        {/* Recommended badge */}
                        {isRec && subject.is_live && (
                          <div className="absolute -top-2.5 left-1/2 -translate-x-1/2">
                            <span className="text-xs font-bold px-2.5 py-0.5 bg-[#C9A227] text-white rounded-full shadow-sm whitespace-nowrap">
                              Recommended
                            </span>
                          </div>
                        )}

                        <div className="flex items-start justify-between mb-3 mt-1">
                          <div className={`w-10 h-10 rounded-xl ${bg} flex items-center justify-center`}>
                            <span className="text-white font-bold text-base">{lt}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            {subject.hot_questions > 0 && (
                              <span className="flex items-center gap-0.5 text-xs font-bold px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded-full">
                                <Flame className="w-2.5 h-2.5" />{subject.hot_questions} hot
                              </span>
                            )}
                            {subject.is_live
                              ? <span className="text-xs font-semibold px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full">Live</span>
                              : <span className="text-xs font-semibold px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full">Soon</span>
                            }
                          </div>
                        </div>

                        <h3 className="font-bold text-[#2E3A59] text-base mb-1">{subject.name}</h3>
                        <p className="text-xs text-slate-500 mb-3">
                          {subject.question_count} PYQs · 2021–2025{subject.topic_count > 0 ? ` · ${subject.topic_count} topics` : ""}
                        </p>
                        {unans > 0 && subject.is_live && (
                          <p className="text-xs text-slate-400 mb-3">{unans} unanswered PYQs from 2021–2025</p>
                        )}

                        {subject.is_live && (
                          <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                            <span className="text-xs font-semibold text-[#2E3A59] group-hover:text-[#C9A227] transition-colors">Start Practice</span>
                            <span className="text-slate-400 text-sm">–</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Recent Attempts */}
              <div className="bg-white rounded-xl border border-slate-100 p-4 lg:p-6" data-testid="recent-attempts">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-[#2E3A59] flex items-center gap-2">
                    <History className="w-5 h-5 text-[#C9A227]" /> Recent Attempts
                  </h3>
                  <button onClick={() => navigate("/attempts")} className="flex items-center gap-1 text-xs text-slate-500 hover:text-[#2E3A59] font-medium">
                    View All <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>

                {recentAttempts.length > 0 ? (
                  <div className="space-y-2">
                    {recentAttempts.slice(0, 5).map((attempt, i) => {
                      const pct     = attempt.evaluation?.percentage || 0;
                      const awarded = getMarksAwarded(attempt);
                      const total   = getMarksTotal(attempt);
                      const label   = getScoreLabel(pct);
                      const bg      = SUBJECT_BG[attempt.subject] || "bg-slate-400";

                      return (
                        <div
                          key={attempt.attempt_id || i}
                          className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl hover:bg-slate-100 cursor-pointer transition-colors"
                          onClick={() => viewAttemptDetail(attempt)}
                        >
                          <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center flex-shrink-0`}>
                            <span className="text-white font-bold text-sm">{SUBJECT_LETTER[attempt.subject] || "?"}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-[#2E3A59] truncate">
                              {attempt.question_snapshot?.question_text?.substring(0, 55) || attempt.question_text?.substring(0, 55) || "Question"}...
                            </p>
                            <p className="text-xs text-slate-500">
                              {attempt.subject} · {formatDate(attempt.student_answer?.submitted_at || attempt.submitted_at)}
                              {attempt.attempt_number > 1 && <span className="ml-1 text-purple-500 font-medium">· Attempt #{attempt.attempt_number}</span>}
                            </p>
                          </div>
                          <div className="text-right flex-shrink-0 min-w-[72px]">
                            <p className={`text-sm font-bold ${getScoreColor(pct)}`}>
                              {awarded !== null ? awarded : pct + "%"}/{total !== null ? total : "—"}
                            </p>
                            <p className={`text-xs font-medium ${label.color}`}>{label.text}</p>
                          </div>
                          <Eye className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        </div>
                      );
                    })}
                    {coach && (
                      <p className="text-xs text-slate-500 pt-2 px-1 italic">
                        You are improving on the same question. Keep going. One more {coach.name} answer today will help strengthen your writing pattern.
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <History className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                    <p className="text-sm text-slate-400">No attempts yet. Start practicing!</p>
                    <Button onClick={() => subjects[0]?.is_live && navigate(`/practice/${subjects[0].name}`)} className="mt-4 bg-[#2E3A59]">Start Practice</Button>
                  </div>
                )}
              </div>

              {/* Performance Overview — visible after 30 days of account creation */}
              {(() => {
                const daysSinceJoined = user?.created_at
                  ? Math.floor((new Date() - new Date(user.created_at)) / (1000 * 60 * 60 * 24))
                  : 0;
                const showPerformance = daysSinceJoined >= 30;
                if (!showPerformance) return (
                  <div className="bg-white rounded-xl border border-slate-100 p-5 text-center" data-testid="performance-locked">
                    <BarChart3 className="w-10 h-10 text-slate-200 mx-auto mb-3" />
                    <p className="text-sm font-semibold text-[#2E3A59] mb-1">Performance Overview</p>
                    <p className="text-xs text-slate-400">
                      Unlocks after 30 days of practice. {Math.max(0, 30 - daysSinceJoined)} days to go!
                    </p>
                  </div>
                );
                return null;
              })()}
              {(() => {
                const daysSinceJoined = user?.created_at
                  ? Math.floor((new Date() - new Date(user.created_at)) / (1000 * 60 * 60 * 24))
                  : 0;
                const showPerformance = daysSinceJoined >= 30;
                if (!showPerformance) return null;
                return (
              <div className="bg-white rounded-xl border border-slate-100 p-4 lg:p-6" data-testid="progress-card">
                <h3 className="font-semibold text-[#2E3A59] mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-[#C9A227]" /> Performance Overview
                </h3>
                {isNewUser ? (
                  <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#2E3A59] to-[#1A2236] flex items-center justify-center mb-5">
                      <Play className="w-7 h-7 text-white" />
                    </div>
                    <h4 className="text-lg font-bold text-[#2E3A59] mb-2">Your first question is waiting.</h4>
                    <p className="text-slate-500 text-sm mb-6 max-w-sm">Start practising now and see exactly what earns marks — broken down point by point.</p>
                    <Button onClick={() => navigate("/practice")} className="bg-[#2E3A59] h-11 px-8 font-semibold" data-testid="first-practice-cta">
                      <Play className="w-4 h-4 mr-2" /> Start Practising
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
                      <div className="text-center p-3 lg:p-4 bg-slate-50 rounded-xl">
                        <div className="text-xl lg:text-2xl font-bold text-[#2E3A59]">{stats?.week_attempted || 0}</div>
                        <div className="text-xs text-slate-500">This week</div>
                      </div>
                      <div className="text-center p-3 lg:p-4 bg-slate-50 rounded-xl">
                        <div className="text-xl lg:text-2xl font-bold text-[#2E3A59]">{stats?.average_score || 0}%</div>
                        <div className="text-xs text-slate-500">Avg score</div>
                      </div>
                      <div className="text-center p-3 lg:p-4 bg-slate-50 rounded-xl col-span-2 lg:col-span-1">
                        <div className={`text-xs lg:text-sm font-semibold px-2 py-1 rounded-full inline-block ${getReadinessColor(stats?.readiness)}`}>
                          {getReadinessLabel(stats?.readiness)}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">Readiness</div>
                      </div>
                    </div>
                    <div className="grid lg:grid-cols-2 gap-4">
                      <div className="p-4 bg-amber-50/50 rounded-xl border border-amber-100">
                        <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" />Weak Topics</h4>
                        {stats?.weak_topics?.length > 0
                          ? <div className="flex flex-wrap gap-2">{stats.weak_topics.slice(0, 4).map((t, i) => <span key={i} className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded-full">{t}</span>)}</div>
                          : <p className="text-xs text-slate-400">Practice to identify weak areas</p>}
                      </div>
                      <div className="p-4 bg-emerald-50/50 rounded-xl border border-emerald-100">
                        <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-500" />Strong Topics</h4>
                        {stats?.strong_topics?.length > 0
                          ? <div className="flex flex-wrap gap-2">{stats.strong_topics.slice(0, 4).map((t, i) => <span key={i} className="text-xs px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full">{t}</span>)}</div>
                          : <p className="text-xs text-slate-400">Keep practising!</p>}
                      </div>
                    </div>
                  </>
                )}
              </div>
                );
              })()}

              {/* Sample report */}
              <a href="/Falco_Sample_Dashboard_Report.pdf" target="_blank" rel="noopener noreferrer"
                className="group flex items-center justify-between p-4 lg:p-5 bg-gradient-to-r from-[#FDF8E6] to-[#FFF9F0] rounded-xl border border-[#C9A227]/25 hover:border-[#C9A227]/60 transition-all"
                data-testid="sample-report-teaser">
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-xl bg-[#C9A227]/15 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-[#C9A227]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#2E3A59]">See what your dashboard looks like after 30 days of practice</p>
                    <p className="text-xs text-[#A6851D] mt-0.5">View Sample Report →</p>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-[#C9A227] group-hover:translate-x-1 transition-transform flex-shrink-0" />
              </a>

              {/* Repeated Mistakes — gated behind 30 days */}
              {(() => {
                const days = user?.created_at
                  ? Math.floor((new Date() - new Date(user.created_at)) / (1000 * 60 * 60 * 24))
                  : 0;
                if (days < 30 || !stats?.repeated_misses?.length) return null;
                return (
                  <div className="bg-white rounded-xl border border-slate-100 p-4 lg:p-6">
                    <h3 className="font-semibold text-[#2E3A59] mb-4 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-500" /> Repeated Mistakes
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {stats.repeated_misses.slice(0, 4).map((miss, i) => (
                        <div key={i} className="p-3 bg-red-50 rounded-lg border border-red-100">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-mono font-medium text-red-700">{miss.code}</span>
                            <span className="text-xs font-semibold text-red-600 bg-red-100 px-2 py-0.5 rounded-full">{miss.count}x</span>
                          </div>
                          {miss.detail && <p className="text-xs text-red-600 line-clamp-2">{miss.detail}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* ── RIGHT SIDEBAR — matching mockup exactly ── */}
            <div className="lg:col-span-4 space-y-5">

              {/* Your next best step — cream card matching mockup */}
              {recommendedSubject && (
                <div className="bg-[#FFFBF0] border border-[#E8D99A] rounded-xl p-5" data-testid="next-best-step-card">
                  <h3 className="font-semibold text-[#2E3A59] mb-1">Your next best step</h3>
                  <p className="text-sm text-slate-600 mb-4">
                    Continue with {recommendedSubject.name} now. You have the most unanswered questions there, so it is the easiest place to make progress today.
                  </p>
                  <button
                    onClick={() => navigate(`/practice/${recommendedSubject.name}`)}
                    className="w-full py-2.5 bg-white border-2 border-[#2E3A59] text-[#2E3A59] rounded-lg font-semibold text-sm hover:bg-[#2E3A59] hover:text-white transition-colors"
                  >
                    Start {recommendedSubject.name} Practice
                  </button>
                </div>
              )}

              {/* Progress this week */}
              <div className="bg-white rounded-xl border border-slate-100 p-5" data-testid="progress-week-card">
                <h3 className="font-semibold text-[#2E3A59] mb-2">Progress this week</h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {stats?.week_attempted || 0} answer{stats?.week_attempted !== 1 ? "s" : ""} attempted
                  {stats?.average_score ? ` with an average score of ${stats.average_score}%.` : "."}
                  {(stats?.week_attempted || 0) > 0 ? " You are moving in the right direction." : " Start practising to build momentum."}
                </p>
                {(stats?.week_attempted || 0) > 0 && (
                  <button onClick={() => navigate("/attempts")} className="text-sm text-emerald-600 font-semibold mt-2 hover:underline block">
                    See detailed performance →
                  </button>
                )}
              </div>

              {/* Free plan usage — with colored progress bars matching mockup */}
              <div className="bg-white rounded-xl border border-slate-100 p-5" data-testid="free-plan-usage">
                <h3 className="font-semibold text-[#2E3A59] mb-4">Free plan usage</h3>
                <div className="space-y-3">
                  {[
                    { label: "Short answers", used: saUsed, total: 4,  left: 4 - saUsed, barColor: "bg-blue-500" },
                    { label: "Short essays",  used: seUsed, total: 2,  left: 2 - seUsed, barColor: "bg-purple-500" },
                    { label: "Long essays",   used: leUsed, total: 1,  left: 1 - leUsed, barColor: "bg-amber-400" },
                  ].map(({ label, used, total, left, barColor }) => (
                    <div key={label}>
                      <div className="flex items-center justify-between text-xs mb-1.5">
                        <span className="text-slate-600 font-medium">{label}</span>
                        <span className={left === 0 ? "text-red-500 font-semibold" : "text-slate-500"}>
                          {left === 0 ? `Used ${total} of ${total}` : `${left} of ${total} left`}
                        </span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${left === 0 ? "bg-red-400" : barColor}`}
                          style={{ width: `${Math.min(100, Math.round((used / total) * 100))}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mt-3">
                  Upgrade only when you need more practice. For now, you can continue with short answers.
                </p>
                <button
                  onClick={() => setUpgradeOpen(true)}
                  className="mt-3 w-full py-2 text-sm font-semibold text-[#2E3A59] border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors flex items-center justify-center gap-1"
                >
                  Upgrade for more →
                </button>
              </div>

            </div>
          </div>
        </div>
      </main>

      <PaidPlanModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} />

      {/* Attempt Detail Modal — completely unchanged */}
      <Dialog open={attemptDetailOpen} onOpenChange={setAttemptDetailOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Attempt Details</DialogTitle></DialogHeader>
          {selectedAttempt && (() => {
            const awarded = getMarksAwarded(selectedAttempt);
            const total   = getMarksTotal(selectedAttempt);
            const pct     = selectedAttempt.evaluation?.percentage || (awarded !== null && total ? Math.round((awarded / total) * 100) : 0);
            return (
              <div className="py-4 space-y-4">
                <div className="p-4 bg-slate-50 rounded-xl">
                  <p className="text-xs text-slate-500 mb-1">Question{selectedAttempt.attempt_number && <span className="ml-2 text-[#2E3A59] font-semibold">· Attempt #{selectedAttempt.attempt_number}</span>}</p>
                  <p className="text-[#2E3A59] font-medium">{selectedAttempt.question_snapshot?.question_text || selectedAttempt.question_text}</p>
                </div>
                <button
                  onClick={() => {
                    setAttemptDetailOpen(false);
                    const qid = selectedAttempt.question_id || selectedAttempt.question_snapshot?.question_id;
                    if (selectedAttempt.subject && qid) navigate(`/practice/${selectedAttempt.subject}`, { state: { retryQuestionId: qid } });
                    else if (selectedAttempt.subject) navigate(`/practice/${selectedAttempt.subject}`);
                  }}
                  className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                  Retry This Question
                </button>
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div><p className="text-xs text-slate-500">Score</p><p className="text-2xl font-bold text-[#2E3A59]">{awarded !== null ? awarded : "—"}/{total !== null ? total : "—"}</p></div>
                  <div className={`text-3xl font-bold ${getScoreColor(pct)}`}>{pct}%</div>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-2">Your Answer</p>
                  <div className="p-4 bg-white border border-slate-200 rounded-xl max-h-40 overflow-y-auto">
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{selectedAttempt.student_answer?.text || selectedAttempt.student_answer}</p>
                  </div>
                </div>
                <FeedbackPanel fb={selectedAttempt.evaluation?.personalised_feedback || selectedAttempt.evaluation?.feedback} />
                {selectedAttempt.evaluation?.points_covered?.length > 0 && (
                  <div><p className="text-xs text-slate-500 mb-2">Points Covered</p>
                    <div className="space-y-1">{selectedAttempt.evaluation.points_covered.map((pt, i) => (
                      <div key={i} className="flex items-start gap-2 px-3 py-2 bg-emerald-50 border border-emerald-100 rounded-lg"><span className="text-emerald-600 mt-0.5 flex-shrink-0">✓</span><span className="text-xs text-emerald-800">{pt}</span></div>
                    ))}</div>
                  </div>
                )}
                {selectedAttempt.evaluation?.points_missed?.length > 0 && (
                  <div><p className="text-xs text-slate-500 mb-2">Missing Points</p>
                    <div className="space-y-1">{selectedAttempt.evaluation.points_missed.map((pt, i) => (
                      <div key={i} className="flex items-start gap-2 px-3 py-2 bg-red-50 border border-red-100 rounded-lg"><span className="text-red-500 mt-0.5 flex-shrink-0">✗</span><span className="text-xs text-red-800">{pt}</span></div>
                    ))}</div>
                  </div>
                )}
                {(selectedAttempt.evaluation?.reason_codes_triggered || selectedAttempt.evaluation?.reason_codes || []).length > 0 && (
                  <div><p className="text-xs text-slate-500 mb-2">Areas to Improve</p>
                    <div className="space-y-2">{(selectedAttempt.evaluation?.reason_codes_triggered || selectedAttempt.evaluation?.reason_codes || []).map((rc, i) => (
                      <div key={i} className="p-3 bg-amber-50 border border-amber-100 rounded-lg">
                        <span className="text-xs font-mono font-medium text-amber-700">{rc.code || rc}</span>
                        {rc.detail && <p className="text-xs text-amber-600 mt-1">{rc.detail}</p>}
                      </div>
                    ))}</div>
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StudentDashboard;